/**
 * Cliente API para el tab "APIs" de la consola de plataforma (ADR-039).
 *
 * Convenciones de la red: snake_case en request/response (igual que el resto de la API).
 * El secreto NUNCA viene en list/get. Únicamente el endpoint /secret lo expone,
 * previo registro en audit_log por el backend.
 *
 * Proveedores conocidos: anthropic · postmark · smtp · ovh_s3 · stripe · azul · cardnet
 */
import { apiRequest } from './client.js';

// ─────────────────────────────────────────────────────────────────────────────
// Tipos de dominio
// ─────────────────────────────────────────────────────────────────────────────

export type IntegrationStatus = 'configured' | 'not_configured' | 'error';

export type IntegrationCategory = 'ai' | 'email' | 'storage' | 'payments';

export interface Integration {
  id: string;
  /** Identificador del proveedor (anthropic, postmark, smtp, ovh_s3, stripe, azul, cardnet) */
  provider: string;
  category: IntegrationCategory;
  status: IntegrationStatus;
  /** Últimos 4 caracteres del secreto, para identificación rápida sin revelar el secreto */
  last4: string | null;
  /** Metadatos no secretos: modelo activo, bucket, from_email, etc. */
  metadata: Record<string, string | null>;
  last_tested_at: string | null;
  updated_at: string;
}

export interface IntegrationListResponse {
  integrations: Integration[];
}

export interface SecretRevealResponse {
  secret: string;
}

export interface TestConnectionResponse {
  status: IntegrationStatus;
  last_tested_at: string;
  /** Mensaje del proveedor en caso de fallo (opcional) */
  message?: string;
}

export interface UpdateIntegrationBody {
  /** Nuevo valor del secreto. Omitir para actualizar solo metadata. */
  secret?: string;
  metadata?: Record<string, string | null>;
}

// ─────────────────────────────────────────────────────────────────────────────
// API
// ─────────────────────────────────────────────────────────────────────────────

export const platformIntegrationsApi = {
  /**
   * Lista todas las integraciones con estado y last4.
   * El secreto completo NO viene en esta respuesta.
   */
  list: (): Promise<IntegrationListResponse> =>
    apiRequest<IntegrationListResponse>('/api/platform/integrations'),

  /**
   * Actualiza secreto y/o metadata de un proveedor.
   * Devuelve la integración actualizada (sin secreto).
   */
  update: (provider: string, body: UpdateIntegrationBody): Promise<Integration> =>
    apiRequest<Integration>(`/api/platform/integrations/${provider}`, {
      method: 'PUT',
      body,
    }),

  /**
   * Revela el secreto completo de un proveedor.
   * Solo debe llamarse tras confirmación explícita del usuario ("Revelar").
   * El backend registra el acceso en audit_log.
   */
  revealSecret: (provider: string): Promise<SecretRevealResponse> =>
    apiRequest<SecretRevealResponse>(`/api/platform/integrations/${provider}/secret`),

  /**
   * Dispara una prueba de conexión contra el proveedor.
   * Devuelve el status actualizado y el timestamp del test.
   */
  testConnection: (provider: string): Promise<TestConnectionResponse> =>
    apiRequest<TestConnectionResponse>(`/api/platform/integrations/${provider}/test`, {
      method: 'POST',
    }),
};
